import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { AlertTriangle, CheckCircle } from 'lucide-react';

const PredictiveAnalyticsPanel = ({ forecasts }) => {
  const chartData = forecasts?.slice(0, 6)?.map(forecast => ({
    period: forecast?.forecastPeriod,
    predicted: forecast?.predictedOccurrenceCount,
    confidence: forecast?.confidenceLevel * 100,
    costImpact: forecast?.predictedCostImpactKina
  }));

  const getTriggerTypeLabel = (type) => {
    const labels = {
      minimum_wage_breach: 'Minimum Wage Breach',
      overtime_rate_violation: 'Overtime Rate Violation',
      leave_entitlement_breach: 'Leave Entitlement Breach',
      contract_compliance_violation: 'Contract Compliance',
      nasfund_calculation_error: 'NASFUND Error',
      swt_calculation_error: 'SWT Error',
      working_hours_violation: 'Working Hours Violation',
      payment_delay_violation: 'Payment Delay'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Predictive Trend Chart */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Predicted Violation Trends</h3>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorPredicted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Area
              type="monotone"
              dataKey="predicted"
              stroke="#8b5cf6"
              fillOpacity={1}
              fill="url(#colorPredicted)"
              name="Predicted Violations"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Early Warning Alerts */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          Early Warning Alerts
        </h3>
        <div className="space-y-3">
          {forecasts?.filter(f => f?.earlyWarningTriggered)?.slice(0, 3)?.map((forecast, index) => (
            <div key={index} className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-1">
                    {getTriggerTypeLabel(forecast?.violationTriggerType)}
                  </div>
                  <div className="text-xs text-gray-600">
                    Forecast Period: {forecast?.forecastPeriod}
                  </div>
                </div>
                <span className="px-2 py-1 bg-orange-600 text-white text-xs font-bold rounded">
                  {(forecast?.confidenceLevel * 100)?.toFixed(0)}% confidence
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                <div>
                  <div className="text-gray-500 mb-1">Predicted Count</div>
                  <div className="font-bold text-gray-900">{forecast?.predictedOccurrenceCount}</div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Affected Employees</div>
                  <div className="font-bold text-gray-900">{forecast?.predictedAffectedEmployees}</div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Cost Impact</div>
                  <div className="font-bold text-red-600">K{forecast?.predictedCostImpactKina?.toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prevention Recommendations */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Proactive Prevention Strategies</h3>
        <div className="space-y-2">
          {forecasts?.slice(0, 3)?.map((forecast, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-900 mb-2">
                {getTriggerTypeLabel(forecast?.violationTriggerType)}
              </div>
              <div className="space-y-1">
                {forecast?.preventionRecommendations?.slice(0, 2)?.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                    <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </div>
                ))}
              </div>
              {forecast?.estimatedPreventionCostKina > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between text-xs">
                  <span className="text-gray-600">Prevention Investment:</span>
                  <span className="font-semibold text-blue-600">K{forecast?.estimatedPreventionCostKina?.toLocaleString()}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalyticsPanel;